import { useState } from "react";
import Alert from "@shared/v2/Alert";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import axios from "axios";
import { set, trim } from "lodash";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";
import AddEditModalBody from "@shared/AddressModal/AddEditModalBody";
import * as creators from "../actions/creators";
import useAddressStates from "../../PersonDetail/shared/useAddressStates";
import InvalidAddressModal from "../../PersonDetail/components/InvalidAddressModal";
import VerifyAddressModal from "../../PersonDetail/components/VerifyAddressModal";
import { getSuggestedAddressCheck } from "../../PersonDetail/shared/api";
import { DEFAULT_INVALID_ADDRESS, DEFAULT_VERIFY_ADDRESS } from "../../PersonDetail/components/ContactInfo";

const getDefaultOfficeModel = () => ({
  name: "",
  phone: "",
  fax: "",
});

const NewOfficeModal = ({ addOfficeSuccess, onClose, open, setShowOfficeModal }) => {
  const [formData, _setFormData] = useState(getDefaultOfficeModel());
  const [invalidAddress, setInvalidAddress] = useState(null);
  const [verifyAddress, setVerifyAddress] = useState(DEFAULT_VERIFY_ADDRESS);

  const {
    streetAddress,
    secondaryAddress,
    city,
    locality,
    postalCode,
    isLoading,
    error,
    country,
    specificTextInputError,
    onCloseHandlr,
    setAddressType,
    setStreetAddress,
    setSecondaryAddress,
    setCity,
    setLocality,
    setPostalCode,
    setIsLoading,
    setCountry,
    setError,
  } = useAddressStates({ usedAddresses: [], initAddress: null, onClose });

  const setFormData = (e) => {
    const { name, value } = e.target;
    _setFormData((p) => set({ ...p }, name, value));
  };

  const resetForm = () => {
    _setFormData(getDefaultOfficeModel());
    setStreetAddress("");
    setSecondaryAddress("");
    setCity("");
    setLocality("");
    setPostalCode("");
    setCountry("");
    setError(null);
  };

  const postOfficeAddress = (address) => {
    axios
      .patch(
        "/api/v4/business_settings",
        {
          account: {
            account_offices_attributes: [
              {
                ...formData,
                address_attributes: {
                  street_address: address.streetAddress,
                  secondary_address: address.secondaryAddress,
                  postal_code: address.postalCode,
                  city: address.city,
                  locality: address.locality,
                  country: address.country,
                },
              },
            ],
          },
        },
        { headers: { "x-csrf-token": window.ReactOnRails.authenticityToken() } },
      )
      .then((resD) => {
        resetForm();
        addOfficeSuccess({ data: resD.data });
        onCloseHandlr();
      })
      .catch(() => {
        setError(true);
      });
  };

  return (
    <>
      <InvalidAddressModal
        address={invalidAddress?.address}
        show={Boolean(invalidAddress?.address)}
        onSubmit={invalidAddress?.onSubmit}
        onEdit={invalidAddress?.onEdit}
        onClose={() => setInvalidAddress(DEFAULT_INVALID_ADDRESS)}
      />
      <VerifyAddressModal
        originalAddress={verifyAddress.originalAddress}
        suggestedAddress={verifyAddress.suggestedAddress}
        onSubmit={verifyAddress.onSubmit}
        onEdit={verifyAddress.onEdit}
        show={Boolean(verifyAddress.originalAddress)}
        onClose={() => setVerifyAddress(DEFAULT_VERIFY_ADDRESS)}
      />
      <Modal
        show={open}
        onHide={onCloseHandlr}
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setIsLoading(true);
            const { res } = await getSuggestedAddressCheck({
              city,
              postalCode,
              streetAddress,
              secondaryAddress,
              locality,
              country,
            });

            if (!res.data?.addressDetails?.standardized) {
              setIsLoading(false);
              onClose();
              if (trim(res.data?.addressDetails?.streetAddress) === "") {
                setInvalidAddress({
                  address: {
                    city,
                    postalCode,
                    streetAddress,
                    secondaryAddress,
                    locality,
                    country,
                  },
                  onEdit: () => {
                    setShowOfficeModal(true);
                  },
                  onSubmit: async () => {
                    await postOfficeAddress({
                      city,
                      postalCode,
                      streetAddress,
                      secondaryAddress,
                      locality,
                      country,
                    });
                  },
                });
              } else {
                setVerifyAddress({
                  originalAddress: {
                    city,
                    postalCode,
                    streetAddress,
                    secondaryAddress,
                    locality,
                    country,
                  },
                  suggestedAddress: {
                    streetAddress: res.data?.addressDetails?.streetAddress,
                    secondaryAddress: res.data?.addressDetails?.secondaryAddress,
                    city: res.data?.addressDetails?.city,
                    locality: res.data?.addressDetails?.state,
                    postalCode: res.data?.addressDetails?.postalCode,
                    country: res.data?.addressDetails?.country,
                    standardized: true,
                  },
                  onSubmit: async (newPayLoad) => {
                    await postOfficeAddress(newPayLoad);
                  },
                  onEdit: () => {
                    setShowOfficeModal(true);
                  },
                });
              }
            } else {
              await postOfficeAddress({
                city,
                postalCode,
                streetAddress,
                secondaryAddress,
                locality,
                country,
              });
              setIsLoading(false);
              onClose();
              resetForm();
            }
          }}
          className="tw-flex tw-flex-col tw-gap-[32px]"
        >
          <Modal.Header title="Add Office" closeButton={!isLoading} />

          <Modal.Body>
            {error && (
              <Alert
                containerClass="tw-w-full tw-mb-24px"
                variant="error"
                title="Something went wrong"
                text="Failed to add the new office. Please try again."
              />
            )}
            <fieldset disabled={isLoading} className="tw-flex tw-flex-col tw-gap-[24px]">
              <TextInput
                name="name"
                value={formData.name}
                onChange={setFormData}
                label="Office Name"
                isRequired
              />
              <div className="tw-flex tw-gap-[24px]">
                <TextInput
                  name="phone"
                  containerClassName="tw-flex-1"
                  value={formData.phone}
                  onChange={setFormData}
                  label="Phone"
                />
                <TextInput
                  name="fax"
                  containerClassName="tw-flex-1"
                  value={formData.fax}
                  onChange={setFormData}
                  label="Fax"
                />
              </div>
            </fieldset>
          </Modal.Body>
          <AddEditModalBody
            setAddressType={setAddressType}
            setStreetAddress={setStreetAddress}
            streetAddress={streetAddress}
            setSecondaryAddress={setSecondaryAddress}
            secondaryAddress={secondaryAddress}
            city={city}
            setCountry={setCountry}
            country={country}
            setCity={setCity}
            setLocality={setLocality}
            setError={setError}
            locality={locality}
            setPostalCode={setPostalCode}
            postalCode={postalCode}
            error={error}
            specificTextInputError={specificTextInputError}
            standardized
          />
          <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
            <Button onClick={onClose} disabled={isLoading} size="medium" schema="tertiary">
              Cancel
            </Button>
            <Button
              isLoading={isLoading}
              type="submit"
              disabled={formData.name <= "" || streetAddress <= ""}
              size="medium"
            >
              Add
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

NewOfficeModal.propTypes = {
  addOfficeSuccess: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setShowOfficeModal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  model: state.businessSettings.newOfficeModel,
});

export default connect(mapStateToProps, creators)(NewOfficeModal);
